// sort-imports-ignore
import '@opositandopn/ui/styles';

import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';

import { shouldRetryMutation, shouldRetryQuery } from '@opositandopn/http';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import { App } from './App';
import { ErrorBoundary } from './components/core/ErrorBoundary';
import { Login } from './components/core/layout/Login';
import { PwaReloadPopup } from './components/core/popups/PwaReloadPopup';
import { AuthContextProvider } from './context/AuthContext';
import { AxiosContextProvider } from './context/AxiosContext';
import { ConfigurationContextProvider } from './context/ConfigurationContext';
import { FirebaseContextProvider } from './context/FirebaseContext';
import { ProfileContextProvider } from './context/ProfileContext';
import { PwaContextProvider } from './context/PwaContext';
import { ThemeContextProvider } from './context/ThemeContext';
import { Auth } from './middlewares/Auth';
import { WebVitals } from './middlewares/WebVitals';
import { isClientError, notifyError } from './utils/api';

import './styles/index.scss';
import { Toaster } from '@opositandopn/ui';

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      notifyError(error, mutation.meta?.errorMessages);
    },
  }),
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (isClientError(error)) {
        return;
      }

      notifyError(error, query.meta?.errorMessages);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 0 * (60 * 1000), // 0 mins
      gcTime: 0 * (60 * 1000), // 0 mins
      refetchOnWindowFocus: false,
      retry: shouldRetryQuery,
    },
    mutations: {
      gcTime: 0 * (60 * 1000), // 0 mins
      retry: shouldRetryMutation,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <ErrorBoundary>
      <PwaContextProvider>
        <ThemeContextProvider>
          <QueryClientProvider client={queryClient}>
            <ConfigurationContextProvider>
              <FirebaseContextProvider>
                <WebVitals>
                  <AuthContextProvider>
                    <BrowserRouter>
                      <Auth fallback={<Login />}>
                        <AxiosContextProvider>
                          <ProfileContextProvider>
                            <App />
                          </ProfileContextProvider>
                        </AxiosContextProvider>
                      </Auth>
                      <Toaster />
                    </BrowserRouter>
                  </AuthContextProvider>
                </WebVitals>
              </FirebaseContextProvider>
            </ConfigurationContextProvider>
            {/* <ReactQueryDevtools initialIsOpen={false} /> */}
          </QueryClientProvider>
        </ThemeContextProvider>
        <PwaReloadPopup />
      </PwaContextProvider>
    </ErrorBoundary>
  </StrictMode>,
);
